import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./ui/UIElements/Icon";
// Cross-app navigation removed - Connect consolidated into Dashboard
import finitlessLogo from "../assets/finitless-logo.png";
import finitlessIsotype from "../assets/finitless-isotype.png";
import { usePostHog } from "@finitless/shared/hooks";
import {
  getBrandLogo,
  getBrandIcon,
  getBrandName,
  isWhiteLabel,
} from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";

const Sidebar = ({ onClose, isCollapsed }) => {
  const { t } = useTranslation("navigation");
  const brandLogo = getBrandLogo(finitlessLogo);
  const brandIcon = getBrandIcon(finitlessIsotype);
  const brandName = getBrandName(t("footer.copyright"));
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const navRef = useRef(null);
  const { isFeatureEnabled } = usePostHog();
  const stripeConnectPluginEnabled = isFeatureEnabled("stripe_connect_link");
  const campaignsEnabled = isFeatureEnabled("campaigns");
  // const metaAdsEnabled = isFeatureEnabled("meta_ads_enabled");
  const deliveryMenuEnabled = isFeatureEnabled("delivery_menu_enabled");
  // const menuImportersEnabled = isFeatureEnabled("menu_importers_enabled");

  const metaAdsEnabled = true;
  const menuImportersEnabled = true;

  const navigationItems = [
    {
      id: "overview",
      label: t("sidebar.overview"),
      icon: "BarChart3",
      path: "/overview",
    },
    {
      id: "ai-agents",
      label: t("sidebar.aiAgents"),
      icon: "Bot",
      path: "/ai-agents",
    },
    {
      id: "orders",
      label: t("sidebar.orders"),
      icon: "ShoppingCart",
      path: "/orders",
    },
    {
      id: "users",
      label: t("sidebar.customers"),
      icon: "Users",
      path: "/users",
    },
    {
      id: "analyze",
      label: t("sidebar.analytics"),
      icon: "TrendingUp",
      hasSubmenu: true,
      submenu: [
        {
          id: "conversations",
          label: t("sidebar.conversations"),
          icon: "MessageCircle",
          path: "/analyze/conversations",
        },
        {
          id: "reports",
          label: t("sidebar.reports"),
          icon: "FileText",
          path: "/analyze/reports",
        },
      ],
    },
    // Store Manager Section (migrated from Connect app)
    {
      id: "store-manager",
      label: t("sidebar.storeManager"),
      icon: "Store",
      hasSubmenu: true,
      submenu: [
        {
          id: "stores",
          label: t("sidebar.stores"),
          icon: "Building2",
          path: "/menu/stores",
        },
        {
          id: "products",
          label: t("sidebar.products"),
          icon: "Package",
          path: "/menu/products",
        },
        {
          id: "brands",
          label: t("sidebar.brands"),
          icon: "Bookmark",
          path: "/menu/brands",
        },
      ],
    },
    // Growth Section (Discounts + future items)
    {
      id: "growth",
      label: t("sidebar.growth"),
      icon: "Rocket",
      hasSubmenu: true,
      submenu: [
        {
          id: "discounts",
          label: t("sidebar.discounts"),
          icon: "Tag",
          path: "/discounts",
        },
        {
          id: "cart-recovery",
          label: t("sidebar.cartRecovery"),
          icon: "RefreshCcw",
          path: "/growth/cart-recovery",
        },
        {
          id: "link-in-bio",
          label: t("sidebar.linkInBio"),
          icon: "Link2",
          path: "/growth/link-in-bio",
        },
        ...(metaAdsEnabled
          ? [
              {
                id: "meta-ads",
                label: t("sidebar.metaAds"),
                icon: "Share2",
                path: "/growth/meta-ads",
              },
            ]
          : []),
        ...(campaignsEnabled
          ? [
              {
                id: "campaigns",
                label: t("sidebar.campaigns"),
                icon: "Megaphone",
                path: "/campaigns",
              },
            ]
          : []),
      ],
    },
    // Delivery Section (migrated from Connect app) - feature flagged
    ...(deliveryMenuEnabled
      ? [
          {
            id: "delivery",
            label: t("sidebar.delivery"),
            icon: "Truck",
            hasSubmenu: true,
            submenu: [
              {
                id: "drivers-managers",
                label: t("sidebar.driversManagers"),
                icon: "UserCog",
                path: "/delivery/drivers-managers",
              },
              {
                id: "delivery-groups",
                label: t("sidebar.deliveryGroups"),
                icon: "Users",
                path: "/delivery/groups",
              },
            ],
          },
        ]
      : []),
    // Integrations Section (migrated from Connect app)
    {
      id: "integrations",
      label: t("sidebar.integrations"),
      icon: "Puzzle",
      hasSubmenu: true,
      submenu: [
        {
          id: "pos",
          label: t("sidebar.posSystems"),
          icon: "Monitor",
          path: "/integrations/pos",
        },
        ...(menuImportersEnabled
          ? [
              {
                id: "menu-importers",
                label: t("sidebar.menuImporters"),
                icon: "Download",
                path: "/integrations/menu-importers",
              },
            ]
          : []),
        {
          id: "sales-channels",
          label: t("sidebar.salesChannels"),
          icon: "ShoppingBag",
          path: "/integrations/sales-channels",
        },
        {
          id: "delivery-services",
          label: t("sidebar.deliveryServices"),
          icon: "Truck",
          path: "/integrations/delivery-services",
        },
      ],
    },
    {
      id: "settings",
      label: t("sidebar.settings"),
      icon: "Settings",
      hasSubmenu: true,
      submenu: [
        {
          id: "settings-basic",
          label: t("sidebar.general"),
          icon: "Globe",
          path: "/settings/basic",
        },
        ...(stripeConnectPluginEnabled
          ? [
              {
                id: "settings-online-payments",
                label: t("sidebar.onlinePayments"),
                icon: "CreditCard",
                path: "/settings/online-payments",
              },
            ]
          : []),
        {
          id: "settings-agent-translations",
          label: t("sidebar.agentTranslations"),
          icon: "Languages",
          path: "/settings/agent-translations",
        },
        {
          id: "settings-admins",
          label: t("sidebar.admins"),
          icon: "Shield",
          path: "/settings/admins",
        },
        ...(!isWhiteLabel()
          ? [
              {
                id: "settings-white-label",
                label: t("sidebar.whiteLabel"),
                icon: "Palette",
                path: "/settings/white-label",
              },
            ]
          : []),
      ],
    },
    ...(!isWhiteLabel()
      ? [
          {
            id: "support",
            label: t("sidebar.support"),
            icon: "HelpCircle",
            path: "/support",
          },
        ]
      : []),
  ];

  const [prevCollapsed, setPrevCollapsed] = useState(isCollapsed);
  if (isCollapsed && !prevCollapsed) {
    setPrevCollapsed(true);
    setExpandedMenus({});
  } else if (!isCollapsed && prevCollapsed) {
    setPrevCollapsed(false);
  }

  useEffect(() => {
    const currentPath = location.pathname;

    navigationItems.forEach((item) => {
      if (item.hasSubmenu) {
        const isActive = item.submenu.some(
          (subItem) =>
            currentPath === subItem.path ||
            currentPath.startsWith(subItem.path + "/"),
        );

        if (isActive) {
          setExpandedMenus((prev) => ({
            ...prev,
            [item.id]: true,
          }));
        }
      }
    });
  }, [location.pathname]);

  const closeAllMenus = useCallback(() => {
    setExpandedMenus({});
  }, []);

  useEffect(() => {
    if (!isCollapsed) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        closeAllMenus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCollapsed, closeAllMenus]);

  const handleNavigation = () => {
    // Cerrar el sidebar en móvil después de navegar
    if (onClose) {
      onClose();
    }
  };

  const toggleSubmenu = (itemId) => {
    setExpandedMenus((prev) => {
      const isOpening = !prev[itemId];
      if (isCollapsed && isOpening) {
        // En modo compacto, solo un flyout abierto a la vez
        return { [itemId]: true };
      }
      return {
        ...prev,
        [itemId]: !prev[itemId],
      };
    });
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + "/"),
    );
  };

  const isItemActive = (item) => {
    if (item.externalUrl) {
      return false;
    }
    if (item.hasSubmenu) {
      return isSubmenuActive(item.submenu);
    }
    return item.path && location.pathname.includes(item.path);
  };

  return (
    <div className="bg-sidebar flex h-full flex-col">
      {/* Logo Section */}
      <div className="sidebar-logo-section flex h-16 items-center justify-between px-2">
        <Link
          to="/overview"
          className="flex w-full items-center justify-center"
          onClick={handleNavigation}
        >
          {isCollapsed
            ? brandIcon && (
                <div className="rounded-md bg-black p-1 dark:bg-transparent">
                  <img
                    src={brandIcon}
                    alt={brandName}
                    className="h-4 w-auto object-contain"
                  />
                </div>
              )
            : brandLogo && (
                <div className="rounded-md bg-black px-3 py-1 dark:bg-transparent">
                  <img
                    src={brandLogo}
                    alt={brandName}
                    className="h-8 w-auto max-w-[160px] object-contain"
                  />
                </div>
              )}
        </Link>
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="hover:bg-accent rounded-lg p-2 transition-colors lg:hidden"
          aria-label={t("sidebar.closeSidebar")}
        >
          <Icon name="X" className="text-muted-foreground h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav
        ref={navRef}
        className={`flex-1 space-y-1 py-4 ${
          isCollapsed ? "px-2 lg:px-2" : "px-4"
        }`}
      >
        {navigationItems.map((item) => {
          const isActive = isItemActive(item);
          const isExpanded = expandedMenus[item.id];
          const anyExpanded =
            isCollapsed && Object.values(expandedMenus).some(Boolean);

          return (
            <div key={item.id} className="group relative">
              {item.hasSubmenu ? (
                <button
                  onClick={() => toggleSubmenu(item.id)}
                  className={`flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } h-9 rounded-md text-sm font-normal transition-colors ${
                    isActive
                      ? "bg-brand-gradient text-white"
                      : "text-sidebar-foreground hover:bg-accent hover:text-white"
                  } `}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-5 w-5" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                  {!isCollapsed && (
                    <Icon
                      name="ChevronDown"
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>
              ) : item.externalUrl ? (
                <a
                  href={item.externalUrl}
                  // target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleNavigation}
                  className={`flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } text-sidebar-foreground hover:bg-accent h-9 rounded-md text-sm font-normal transition-colors hover:text-white`}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-5 w-5" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                </a>
              ) : (
                <Link
                  to={item.path}
                  onClick={handleNavigation}
                  className={`flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } h-9 rounded-md text-sm font-normal transition-colors ${
                    isActive
                      ? "bg-brand-gradient text-white"
                      : "text-sidebar-foreground hover:bg-accent hover:text-white"
                  } `}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-5 w-5" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                </Link>
              )}

              {/* Submenu */}
              {item.hasSubmenu && !isCollapsed && (
                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="ms-4 mt-1 space-y-0.5">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.id}
                        to={subItem.path}
                        onClick={() => {
                          handleNavigation();
                        }}
                        className={`flex h-9 w-full items-center rounded-md px-2 text-sm font-normal transition-colors ${
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-muted-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent"
                        } `}
                      >
                        <Icon name={subItem.icon} className="h-4 w-4" />
                        <span className="ms-3 whitespace-nowrap">
                          {subItem.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Tooltip for collapsed mode - only on desktop */}
              {isCollapsed && !item.hasSubmenu && (
                <div className="bg-popover text-popover-foreground pointer-events-none absolute start-full top-0 z-50 ms-2 hidden items-center rounded px-3 py-2 text-sm whitespace-nowrap opacity-0 transition-opacity group-hover:opacity-100 lg:flex">
                  {item.label}
                </div>
              )}

              {/* Popover submenu for collapsed mode (desktop) */}
              {isCollapsed && item.hasSubmenu && (
                <div
                  className={`bg-popover border-border absolute start-full top-0 z-50 ms-2 hidden w-56 rounded-md border shadow-lg transition-opacity lg:block ${
                    isExpanded ? "opacity-100" : "opacity-0"
                  } ${
                    isExpanded
                      ? "pointer-events-auto"
                      : anyExpanded
                        ? "pointer-events-none"
                        : "pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100"
                  }`}
                  role="menu"
                  aria-label={`${item.label} submenu`}
                >
                  <div className="border-border border-b px-3 py-2">
                    <div className="text-popover-foreground text-sm font-medium">
                      {item.label}
                    </div>
                  </div>
                  <div className="py-1">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.id}
                        to={subItem.path}
                        onClick={() => {
                          handleNavigation();
                          setExpandedMenus((prev) => ({
                            ...prev,
                            [item.id]: false,
                          }));
                        }}
                        className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-muted-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent"
                        }`}
                      >
                        <Icon name={subItem.icon} className="h-4 w-4" />
                        <span className="whitespace-nowrap">
                          {subItem.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Section - could add user info or additional links here */}
      <div className="border-border border-t p-3">
        <div className="text-muted-foreground text-center text-xs">
          © {new Date().getFullYear()} {brandName}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
